import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { parseURLParams } from "@/utils/url";
import { setPendingSelection } from "@/store/slices/pendingSelection/pendingSelectionSlice";

/**
 * Fires exactly once after bootstrap data (brands + markets) is ready.
 *
 * Reads the current URL search params, derives the module from the first
 * pathname segment, and dispatches `setPendingSelection` with that payload.
 *
 * From there the full Pending Selection → RTK Listener → merge + validate →
 * update URL → useURLToStore → syncFromURL → userSelection flow takes over
 * automatically.
 *
 * Must be rendered inside the Router tree (requires `useLocation`).
 */
export const useInitialURLBootstrap = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const hasBootstrapped = useRef(false);

  const isDataFetched = useSelector(
    (state: RootState) => state.activeGroup.bootstrapDataStatus.isDataFetched,
  );

  useEffect(() => {
    if (!isDataFetched || hasBootstrapped.current) return;

    // Guard: fire exactly once
    hasBootstrapped.current = true;

    const searchParams = new URLSearchParams(location.search);
    const parsed = parseURLParams(searchParams);

    // Derive module from the first pathname segment (e.g. "/equity/brand" → "equity")
    const module = location.pathname.split("/")?.[1] ?? "";

    dispatch(
      setPendingSelection({
        ...parsed,
        ...(module ? { module } : {}),
      } as any),
    );
  }, [isDataFetched]);
};

export default useInitialURLBootstrap;